import { Workflow as WorkflowIcon, FileEdit, UserCheck, Plus, PanelRight, LayoutTemplate, ArrowRight, FlaskConical } from "lucide-react";
import { useHive } from "@/store/useHive";
import { useWorkflow } from "@/store/workflowStore";
import { useExecution } from "@/store/executionStore";
import { useSampleDataCta } from "@/hooks/useSampleDataCta";
import { SampleBadge } from "@/components/ui/SampleBadge";

const accent = "#f43f5e";

/** Compact desktop surface — the workflow factory at a glance. */
export function WorkflowWidget() {
  const openApp = useHive((s) => s.openApp);
  const workflows = useWorkflow((s) => s.workflows);
  const newWorkflow = useWorkflow((s) => s.newWorkflow);
  const setTemplatesOpen = useWorkflow((s) => s.setTemplatesOpen);
  const executions = useExecution((s) => s.executions);
  const sampleCta = useSampleDataCta();

  const active = workflows.filter((w) => w.status === "active").length;
  const drafts = workflows.filter((w) => w.status === "draft").length;
  const needApproval = workflows.filter((w) => w.status === "needs-approval").length;
  const runs = workflows.reduce((n, w) => n + w.runsThisWeek, 0);
  const failed = executions.filter((e) => e.status === "failed").length;
  const top = [...workflows].sort((a, b) => b.runsThisWeek - a.runsThisWeek)[0];

  const stop = (e: React.PointerEvent) => e.stopPropagation();

  const cells = [
    { n: drafts, l: "drafts", color: "#94a3b8", icon: FileEdit },
    { n: needApproval, l: "approval", color: "#a855f7", icon: UserCheck },
    { n: failed, l: "failed", color: "#f43f5e", icon: WorkflowIcon },
  ];

  // Fresh accounts: no automations yet — pitch the value, offer the demo.
  if (workflows.length === 0) {
    return (
      <div className="w-[330px] cursor-pointer" onClick={() => openApp("workflows")}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-medium text-app-2">
            <WorkflowIcon size={14} style={{ color: accent }} /> Workflows
          </div>
          <span className="text-[11px] text-app-2 tabular-nums">0 runs · 7d</span>
        </div>
        <div className="rounded-xl border border-dashed border-app bg-surface-2/40 p-4 text-center">
          <div className="text-sm font-semibold text-app">No workflows yet</div>
          <div className="mt-1 text-xs text-app-2">
            Chain a Hook, Processes and a Publisher into automations that run your busywork.
          </div>
          <button
            onPointerDownCapture={stop}
            onClick={(e) => { e.stopPropagation(); openApp("workflows"); setTemplatesOpen(true); }}
            className="mt-3 inline-flex items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-white transition hover:brightness-110"
            style={{ background: accent }}
          >
            <LayoutTemplate size={12} /> Start from a template
          </button>
          {sampleCta && (
            <button
              onPointerDownCapture={stop}
              onClick={(e) => { e.stopPropagation(); sampleCta.run(); }}
              className="mt-1.5 inline-flex items-center justify-center gap-1 rounded-lg border border-app px-3 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
            >
              <FlaskConical size={12} /> {sampleCta.label}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[330px] cursor-pointer" onClick={() => openApp("workflows")}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-app-2">
          <WorkflowIcon size={14} style={{ color: accent }} /> Workflows
        </div>
        <span className="text-[11px] text-app-2 tabular-nums">{runs} runs · 7d</span>
      </div>

      <div className="flex items-end gap-3">
        <div>
          <div className="text-4xl font-semibold leading-none text-app tabular-nums">{active}</div>
          <div className="mt-1 text-[11px] text-app-2">active workflows</div>
        </div>
        <div className="mb-0.5 grid flex-1 grid-cols-3 gap-1.5">
          {cells.map((c) => (
            <div key={c.l} className="rounded-lg border border-app bg-surface-2/40 py-1.5 text-center">
              <div className="flex items-center justify-center gap-1 text-sm font-semibold tabular-nums" style={{ color: c.color }}>
                <c.icon size={11} /> {c.n}
              </div>
              <div className="text-[9px] uppercase tracking-wide text-app-2">{c.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* top workflow */}
      {top && (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-app bg-surface-2/40 px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-app-2">Top</span>
          <span className="truncate text-[11px] font-medium text-app">{top.name}</span>
          {top.isSample && <SampleBadge />}
          <ArrowRight size={12} className="ml-auto shrink-0 text-app-2" />
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-1.5">
        <button
          onPointerDownCapture={stop}
          onClick={(e) => { e.stopPropagation(); newWorkflow(); openApp("workflows"); }}
          className="flex items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] font-semibold text-white transition hover:brightness-110"
          style={{ background: accent }}
        >
          <Plus size={12} /> New
        </button>
        <button
          onPointerDownCapture={stop}
          onClick={(e) => { e.stopPropagation(); openApp("workflows"); }}
          className="flex items-center justify-center gap-1 rounded-lg border border-app bg-surface-2/60 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
        >
          <PanelRight size={12} /> Builder
        </button>
        <button
          onPointerDownCapture={stop}
          onClick={(e) => { e.stopPropagation(); openApp("workflows"); setTemplatesOpen(true); }}
          className="flex items-center justify-center gap-1 rounded-lg border border-app bg-surface-2/60 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
        >
          <LayoutTemplate size={12} /> Templates
        </button>
      </div>
    </div>
  );
}
